import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, type ApprovalReason, type Currency, type Lang, type PricingResult } from '@atek/estimate-pricing';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { uiLang } from '../../lib/format';
import type { LivePricing } from '../../lib/livePricing';
import type { EstimateStatus } from '../../types';

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-2', strong && 'font-semibold')}>
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function reasonLabel(reason: ApprovalReason, result: PricingResult, lang: Lang): string {
  if (reason.category === 'budget') return result.budgets.find((b) => b.key === reason.key)?.label ?? reason.key;
  return result.fullScope.lines.find((l) => l.key === reason.key)?.label[lang] ?? reason.key;
}

/** A preview priced in the browser with the server's own package; saved numbers always come from the server. */
interface Props {
  live: LivePricing;
  currency: Currency;
  dirty: boolean;
  /** Past pending_approval, the reasons were approved: list them without asking for approval again. */
  status: EstimateStatus;
}

export function LiveTotalsPanel({ live, currency, dirty, status }: Props) {
  const { t, i18n } = useTranslation('crm');
  const lang = uiLang(i18n.language);
  const money = (amount: number) => formatMoney(amount, currency, lang);
  const { result } = live;
  const awaitingDecision = status === 'draft' || status === 'pending_approval';

  let body: ReactNode;
  if (!result && live.errors.length === 0) {
    // Nothing to price with: the page passes no result and no field errors when the price book is missing.
    body = <p className="text-muted-foreground">{t('estimates.totals.noPriceBook')}</p>;
  } else if (!result) {
    body = <p role="alert" className="text-destructive">{t('estimates.totals.invalid', { n: live.errors.length })}</p>;
  } else {
    const budgetLabel = (key?: string) => result.budgets.find((b) => b.key === key)?.label ?? key ?? '';
    body = (
      <>
        <p className="text-xs text-muted-foreground">
          {t('estimates.totals.counts', {
            transmitters: result.counts.transmitters, sensors: result.counts.managedSensors, gateways: result.counts.gateways,
          })}
        </p>
        {result.budgets.map((b) => (
          <div key={b.key} data-testid={`totals-${b.key}`} className="space-y-1 rounded-md border p-2">
            <p className="font-medium">{b.label}</p>
            <Row label={t('estimates.totals.hardware')} value={money(b.hardware)} />
            <Row label={t('estimates.totals.setup')} value={money(b.setup)} />
            <Row label={t('estimates.totals.subscription')} value={money(b.subscription)} />
            <Row label={t('estimates.totals.year1')} value={money(b.year1)} strong />
            <Row label={t('estimates.totals.year5')} value={money(b.year5)} />
            <Row label={t('estimates.totals.perSensorY1')} value={money(b.perSensorY1)} />
          </div>
        ))}
        {result.warnings.length > 0 && (
          <ul className="space-y-1 text-xs">
            {result.warnings.map((w) => (
              <li key={`${w.code}-${w.budgetKey ?? ''}`} className={w.blocking ? 'text-destructive' : 'text-warning-dark'}>
                {t(`estimates.totals.warnings.${w.code}`, { label: budgetLabel(w.budgetKey) })}
              </li>
            ))}
          </ul>
        )}
        {result.approvalReasons.length > 0 && (
          <div className={cn('rounded-md border p-2 text-xs', awaitingDecision ? 'border-warning bg-warning-light' : 'bg-muted/40')}>
            <p className="font-semibold">{awaitingDecision ? t('estimates.totals.needsApproval') : t('estimates.totals.approvedReasons')}</p>
            <ul className="list-disc pl-4">
              {result.approvalReasons.map((r) => (
                <li key={`${r.category}-${r.key}`}>
                  {t('estimates.totals.reason', { label: reasonLabel(r, result, lang), discount: r.discountPct, limit: r.limitPct })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  return (
    <Card role="region" aria-label={t('estimates.totals.title')}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('estimates.totals.title')}</CardTitle>
        {dirty && <p className="text-xs text-muted-foreground">{t('estimates.totals.unsaved')}</p>}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">{body}</CardContent>
    </Card>
  );
}
